/* eslint-disable jsx-a11y/label-has-for */
import * as React from 'react';
import classnames from 'classnames';
import debounce from 'lodash/debounce';

import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import Input from 'components/inputs/input/Input';
import ListDashboardAddListButton from 'modules/lists/components/ListDashboardAddListButton';
import Tooltip from 'components/Tooltip';
import ItemsPerPage from 'components/items-per-page/ItemsPerPage';

import { LIST_DASHBOARD_QUERY_PARAMS, getQueryValueFromUrl } from 'utils/list';
import { updateQueryParams } from 'utils/location';
import { __t, applyTranslations } from 'utils/language';

import './lists-dashboard-filters.scss';

import type {
  Data,
  LocationWindow,
  MixedSelectDropdownItem,
  SelectOption,
  TextEntry
} from 'types';

applyTranslations(require('./lists-dashboard-filters.i18n.json'));

type Props = {
  location: LocationWindow,
  sortOptions: Array<SelectOption>,
  withSortOnly?: boolean,
  onAddList?: () => void
};

type State = {
  searchTerm: string,
  perPage: string
};

const { SEARCH, SORT, PER_PAGE, PAGE } = LIST_DASHBOARD_QUERY_PARAMS;

class ListDashboardFilters extends React.PureComponent<Props, State> {
  state = {
    searchTerm: getQueryValueFromUrl(this.props.location, SEARCH),
    perPage: getQueryValueFromUrl(this.props.location, PER_PAGE)
  };

  debouncedUpdateSearchQuery = debounce((searchTerm: string, perPage: string, page?: number) => {
    const { location } = this.props;
    updateQueryParams(location, {
      [SEARCH]: searchTerm,
      [PER_PAGE]: perPage,
      [PAGE]: page ?? getQueryValueFromUrl(location, PAGE)
    });
  }, 400);

  getDropdownItems = (sort: string): Array<MixedSelectDropdownItem> => {
    const { sortOptions } = this.props;

    return sortOptions.map(item => ({
      identifier: item.value,
      isDisabled: false,
      isSelected: sort === item.value,
      label: item.label,
      value: item.value
    }));
  };

  handleSortChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { location } = this.props;

    updateQueryParams(location, {
      [SORT]: selectedItem?.identifier || updatedItems[0].identifier
    });
  };

  handleSearchChange = (event: React.SyntheticEvent<TextEntry>): void => {
    const searchTerm = event.currentTarget.value;
    const { perPage } = this.state;

    this.setState(
      { searchTerm },
      () => this.debouncedUpdateSearchQuery(searchTerm, perPage)
    );
  };

  handlePerPageChange = (perPageValue: number): void => {
    const { searchTerm } = this.state;
    const perPage = String(perPageValue);

    this.setState(
      { perPage },
      () => this.debouncedUpdateSearchQuery(searchTerm, perPage, 1)
    );
  };

  render() {
    const { location, withSortOnly, onAddList } = this.props;
    const { searchTerm, perPage } = this.state;
    const sort = getQueryValueFromUrl(location, SORT);
    const dropdownItems = this.getDropdownItems(sort);

    const optionsClasses = classnames('lists-dashboard-filters__options', {
      'lists-dashboard-filters__options--single-option': withSortOnly
    });

    return (
      <div className="lists-dashboard-filters">
        {
          onAddList && (
            <div>
              <div className="lists-dashboard-filters__create">
                <ListDashboardAddListButton onAddList={onAddList} />
              </div>
            </div>
          )
        }
        <div className={optionsClasses}>
          {
            !withSortOnly && (
              <Input
                placeholder={__t('lists_dashboard_filters.search_placeholder')}
                value={searchTerm}
                width="responsive"
                onChange={this.handleSearchChange}
              />
            )
          }
          {
            !withSortOnly && (
              <ItemsPerPage
                perPage={Number(perPage)}
                onChange={this.handlePerPageChange}
              />
            )
          }
          <div className="lists-dashboard-filters__sort">
            <Tooltip
              message={
                searchTerm.length > 0
                  ? __t('lists_dashboard_filters.disabled_sort_tooltip')
                  : undefined
              }
            >
              <React.Fragment>
                <label
                  className="lists-dashboard-filters__sort-label"
                  htmlFor="list-dashboard-sort"
                >
                  {__t('lists_dashboard_filters.sort_label')}:
                </label>
                <MixedSelectDropdown
                  key={`lists-dashboard-filters__dropdown--${sort}`}
                  name="lists-dashboard-filters__dropdown"
                  items={dropdownItems}
                  isDisabled={searchTerm.length > 0}
                  withSelectedInList
                  className="lists-dashboard-filters__dropdown"
                  onItemsToggle={this.handleSortChange}
                />
              </React.Fragment>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  }
}

export default ListDashboardFilters;
